<template>
  <div class="optout-container">
    <!-- 1. LOADING STATE -->
    <div v-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Verifying your information...</p>
    </div>
    
    <!-- 2. ERROR STATE -->
    <div v-else-if="error" class="error-card">
      <h3>{{ errorTitle }}</h3>
      <p>{{ errorMessage }}</p>
      <p class="sub-text">If you believe this is a mistake, please contact support.</p>
    </div>

    <!-- 3. SUCCESS STATE (After submission) -->
    <div v-else-if="submitted" class="profile-card">
      <div class="success-icon">✓</div>
      <h1>Preference Updated</h1>
      <p class="invite-text">
        {{ successMessage }}
      </p>
      <p class="sub-text">You can close this window now.</p>
    </div>

    <!-- 4. MAIN OPT-OUT FORM -->
    <div v-else class="profile-card">
      <div class="card-header">
        <h1>Hello, {{ candidateName }}</h1>
        <p class="subtitle">We're sorry to see you go. Please choose how you would like to proceed regarding <strong>{{ orgName }}</strong>.</p>
      </div>
      
      <div class="card-body">
        
        <!-- Selection Form -->
        <div class="choices-container">
            
            <!-- Option 1: Hierarchy Only -->
            <label class="choice-box" :class="{ active: selectedOption === 'hierarchy' }">
                <input type="radio" value="hierarchy" v-model="selectedOption" />
                <div class="choice-content">
                    <span class="choice-title">Withdraw from Organization Only</span>
                    <p class="choice-desc">
                        Remove me from the <strong>{{ orgName }}</strong> hierarchy structure. 
                        I will remain in the general Talent Pool for future opportunities with other companies.
                    </p>
                </div>
            </label>

            <!-- Option 2: Full Delete -->
            <label class="choice-box warning" :class="{ active: selectedOption === 'full' }">
                <input type="radio" value="full" v-model="selectedOption" />
                <div class="choice-content">
                    <span class="choice-title">Delete Profile & Unsubscribe</span>
                    <p class="choice-desc">
                        Permanently delete my data. I will be removed from the organization hierarchy and the Talent Pool completely.
                    </p>
                </div>
            </label>
        </div>
        
        <div class="actions">
          <button 
            @click="submitOptOut" 
            class="btn-confirm"
            :disabled="!selectedOption || submitting"
          >
            <span v-if="submitting">Processing...</span>
            <span v-else>Confirm Preference</span>
          </button>
        </div>

        <p class="sub-text footer-note">
            This action cannot be undone if you choose to delete your profile.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      loading: true,
      submitting: false,
      submitted: false,
      error: false,
      errorTitle: 'Link Expired',
      errorMessage: 'This opt-out link is no longer valid.',
      candidateName: '',
      orgName: '',
      selectedOption: null, // 'hierarchy' or 'full'
      successMessage: ''
    }
  },
  computed: {
    token() {
      return this.$route.query.token;
    }
  },
  async mounted() {
    // 1. Verify Token and Get Name (Reuse the invite-details endpoint just to get the name)
    if (!this.token) {
      this.error = true;
      this.loading = false;
      return;
    }

    try {
      // We use the same endpoint as the claim page to verify who the user is first
      const response = await axios.get(`http://localhost:8000/api/invite-details/${this.token}`);
      
      if (response.data.valid) {
        this.candidateName = response.data.first_name;
        this.orgName = response.data.organization_name;
      } else {
        this.error = true;
      }
    } catch (e) {
      this.error = true;
      console.error("Verification Error:", e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async submitOptOut() {
        if (!this.selectedOption) return;

        this.submitting = true;

        try {
            // Send the choice to your backend
            // You will need to create this POST endpoint in FastAPI
            await axios.post(`http://localhost:8000/api/opt-out`, {
                token: this.token,
                type: this.selectedOption // 'hierarchy' or 'full'
            });

            // Set success message based on choice
            if (this.selectedOption === 'hierarchy') {
                this.successMessage = `You have been removed from the ${this.orgName} hierarchy. Your profile remains active in the Talent Pool.`;
            } else {
                this.successMessage = "Your profile has been permanently deleted and you have been unsubscribed from all lists.";
            }

            this.submitted = true;

        } catch (e) {
            alert("An error occurred while processing your request. Please try again.");
            console.error(e);
        } finally {
            this.submitting = false;
        }
    }
  }
}
</script>

<style scoped>
/* Base Layout (Shared with ClaimProfile) */
.optout-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f4f6f8;
  font-family: 'Arial', sans-serif;
  padding: 20px;
}

.profile-card, .error-card {
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.05);
  text-align: center;
  max-width: 550px; /* Slightly wider for options */
  width: 100%;
}

/* Headers */
.card-header h1 { margin-bottom: 10px; color: #2c3e50; }
.subtitle { color: #666; margin-bottom: 25px; line-height: 1.5; font-size: 0.95rem; }

/* Choice Boxes Styling */
.choices-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 30px;
    text-align: left;
}

.choice-box {
    display: flex;
    align-items: flex-start;
    padding: 15px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.choice-box:hover {
    border-color: #bdc3c7;
    background-color: #fafafa;
}

.choice-box.active {
    border-color: #f1c40f; /* Brand Color */
    background-color: #fffcf0;
}

/* Specific style for the Delete option when active */
.choice-box.warning.active {
    border-color: #e74c3c;
    background-color: #fdf0ef;
}

.choice-box input {
    margin-top: 5px;
    margin-right: 15px;
    accent-color: #f1c40f;
}

.choice-title {
    display: block;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 5px;
}

.choice-desc {
    font-size: 0.85rem;
    color: #7f8c8d;
    margin: 0;
    line-height: 1.4;
}

/* Buttons */
.btn-confirm {
  background-color: #2c3e50; /* Darker for confirmation */
  color: white;
  border: none;
  padding: 15px 40px;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 100%;
}
.btn-confirm:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
}
.btn-confirm:hover:not(:disabled) {
    background-color: #34495e;
}

/* Footer & Extras */
.footer-text { margin-top: 20px; }
.sub-text { color: #95a5a6; font-size: 0.85rem; margin-top: 15px; }

/* Loading & Success */
.loading-state { text-align: center; color: #7f8c8d; }
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #f1c40f;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}
.success-icon {
    font-size: 3rem;
    color: #27ae60;
    margin-bottom: 20px;
}
.error-card h3 { color: #e74c3c; margin-bottom: 10px; }

@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>